
import { RegisterManager } from "@domain/register/register-manager";
import { DEFAULT_LOCALE } from "@domain/localization/locale-manager";

const INTL_LOCALE_SEPARATOR = "-";
const DAYJS_LOCALE_SEPARATOR = "_";

export const getLanguageFromIntlLocale = (locale: string, lowercase?: boolean): string => {
	const language = locale.split(INTL_LOCALE_SEPARATOR)[0];
	return lowercase ? language.toLowerCase() : language;
};

export function localeToIntlLocale(locale: string): string {
	return locale.replace(DAYJS_LOCALE_SEPARATOR, INTL_LOCALE_SEPARATOR);
}

export function intlLocaleToLocale(locale: string): string {
	return locale.replace(INTL_LOCALE_SEPARATOR, DAYJS_LOCALE_SEPARATOR);
}

export function getLowercaseCountryCodeFromIntlLocale(locale: string): string {
	const parts = locale.split(INTL_LOCALE_SEPARATOR);
	return parts.length === 2 ? parts[1].toLowerCase() : getLowercaseCountryCodeFromIntlLocale(DEFAULT_LOCALE);
}

export function getLowercaseLanguageCodeFromIntlLocale(locale: string): string | null {
	const parts = locale.split(INTL_LOCALE_SEPARATOR);
	return parts.length === 2 ? parts[0].toLowerCase() : null;
}

export function localeKeysToIntlLocale<T>(map: Record<string, T>): Record<string, T> {
	return Object.fromEntries(Object.entries(map).map(([key, value]) => [localeToIntlLocale(key), value]));
}

export function localeValuesToIntlLocale<T extends string | symbol | number>(
	map: Record<T, string>,
): Record<T, string> {
	return Object.fromEntries(
		Object.entries(map).map(([key, value]) => [key, localeToIntlLocale(value as string)]),
	) as Record<T, string>;
}

export function applyCountryCodeFromLang(
	registerManager: RegisterManager,
	lang: string | undefined,
	supportedCountries: string[],
): void {
	if (!lang) return;

	const intlLocale = localeToIntlLocale(lang);
	const countryCode = getLowercaseCountryCodeFromIntlLocale(intlLocale);

	if (supportedCountries.includes(countryCode)) {
		registerManager.sendCountryCode(countryCode);
	}
}

export function applyLocaleFromLang(
	registerManager: RegisterManager,
	lang: string | undefined,
	supportedLocales: Map<string, string[]>,
): void {
	if (!lang) return;

	const localExists = Array.from(supportedLocales.values()).some(locales => locales.includes(lang));

	if (localExists) {
		const intlLocale = localeToIntlLocale(lang);
		registerManager.changeLocale(intlLocale);
	}
}

export const getCountryLocalesFromLang = (
	lang: string | undefined,
	supportedLocales: Map<string, string[]>,
): Map<string, string[]> => {
	if (!lang) return new Map();

	const intlLocale = localeToIntlLocale(lang);
	const countryCode = getLowercaseCountryCodeFromIntlLocale(intlLocale);
	const countryLocales = supportedLocales.get(countryCode);

	return countryLocales ? new Map([[countryCode, countryLocales]]) : new Map<string, string[]>();
};

export const getLanguageSelfNameFromIntlLocale = (locale: string): string => {
	const [lang] = locale.toLowerCase().split(INTL_LOCALE_SEPARATOR);
	const displayNames = new Intl.DisplayNames([lang], { type: "language" });
	return displayNames.of(lang) || lang;
};
